import React, { useEffect, useState } from 'react';
import styled from '@emotion/native';
import { mint, sand, white } from '@/shared/styles/colors';
import { BaseText, H3 } from '@/shared/components/StyledText';
import { IsDesktopProps } from '@/shared/styles/types';
import useIsDesktop from '@/shared/hooks/useIsDesktop';
import { useImageRatio } from '@/shared/hooks/useImageRatio';
import { useMutation, useQuery } from '@apollo/client';
import {
  InstantEventType,
  MutationResult,
  MutationStylistAcceptMatchArgs,
  MutationStylistDeclineMatchArgs,
  PricingTier,
  QueryClientArgs,
  QueryInstantStylingRequestArgs,
  QueryResult,
} from '@/shared/types/generated';
import {
  GET_CLIENT,
  GET_INSTANT_STYLING_REQUEST,
  GET_PRICING,
} from '@/shared/queries';
import useInstantStylingStore from '@/stores/useInstantStylingStore';
import useDimensions from '@/shared/hooks/useDimensions';
import ClientProfileModal from '@/shared/components/ClientProfileModal';
import SentryService from '@/services/sentry/Sentry.service';
import { formatCurrency, getDropdownOptionLabel } from '@/shared/utils';
import {
  STYLE_OPTIONS,
  STYLIST_MATCH_EXPIRATION_SECONDS,
} from '@/shared/constants/instantCall';
import BackToTopButton from '@/shared/components/BackToTopButton';
import { useScrollToTop } from '@/shared/hooks/useScrollToTop';
import Spacer from '@/shared/components/Spacer';
import { FlexCenterScreen } from '@/shared/styles/styledComponents/styledComponents';
import { ActivityIndicator } from 'react-native-paper';
import { useShallow } from 'zustand/react/shallow';
import {
  STYLIST_ACCEPT_MATCH,
  STYLIST_DECLINE_MATCH,
} from '@/shared/mutations';
import AcceptTimerSection from '../components/AcceptTimerSection';
import {
  getDurationFromInstantEventType,
  getHourlyRateFromPricingRequest,
  getTimeRemainingInSeconds,
} from '../helpers';
import { useGetUser } from '@/shared/hooks/useGetUser';
import useStore from '@/stores/useStore';
import { router } from 'expo-router';
import { ROUTES } from '@/shared/constants/routes';

interface Props {
  handleStartSession: (guestId: number) => void;
  onStylistDeclineMatch: () => void;
}

const StylistMatchFound = ({
  handleStartSession,
  onStylistDeclineMatch,
}: Props) => {
  const isDesktop = useIsDesktop();
  const { width } = useDimensions();
  const scrollToTop = useScrollToTop();
  const { user } = useGetUser();

  const setSnackbarState = useStore((state) => state.setSnackbarState);
  const { matchStatus, setMatchStatus, resetMatchStatus } =
    useInstantStylingStore(
      useShallow((state) => ({
        matchStatus: state.matchStatus,
        setMatchStatus: state.setMatchStatus,
        resetMatchStatus: state.resetMatchStatus,
      })),
    );

  const [isClientProfileModalVisible, setIsClientProfileModalVisible] =
    useState(false);
  const [timeRemaining, setTimeRemaining] = useState(
    STYLIST_MATCH_EXPIRATION_SECONDS,
  );

  const { data: pricingData } = useQuery<QueryResult<'pricing'>>(GET_PRICING, {
    onError: (error) => {
      SentryService.captureError('Error fetching pricing data', error);
    },
  });

  const { data: requestData, loading: loadingRequest } = useQuery<
    QueryResult<'instantStylingRequest'>,
    QueryInstantStylingRequestArgs
  >(GET_INSTANT_STYLING_REQUEST, {
    skip: !matchStatus?.requestId,
    variables: { input: { id: matchStatus?.requestId ?? 0 } },
    onCompleted: (data) => {
      setTimeRemaining(
        getTimeRemainingInSeconds(
          data.instantStylingRequest.expiresAt
            ? new Date(data.instantStylingRequest.expiresAt)
            : null,
        ),
      );
      if (data.instantStylingRequest?.client?.user?.id) {
        setMatchStatus({
          clientUserId: data.instantStylingRequest.client.user.id,
        });
      }
    },
    onError: (error) => {
      SentryService.captureError(
        'Failed to get instant styling request data for stylist match found',
        error,
      );
    },
  });

  const { data: clientData, loading: loadingClient } = useQuery<
    QueryResult<'client'>,
    QueryClientArgs
  >(GET_CLIENT, {
    skip: !requestData?.instantStylingRequest.clientId,
    variables: {
      input: {
        id: requestData?.instantStylingRequest.clientId ?? 0,
      },
    },
    onError: (error) => {
      SentryService.captureError(
        'Failed to get client data for stylist match found',
        error,
      );
    },
  });

  const [stylistAcceptMatch] = useMutation<
    MutationResult<'stylistAcceptMatch'>,
    MutationStylistAcceptMatchArgs
  >(STYLIST_ACCEPT_MATCH);

  const [stylistDeclineMatch] = useMutation<
    MutationResult<'stylistDeclineMatch'>,
    MutationStylistDeclineMatchArgs
  >(STYLIST_DECLINE_MATCH);

  const isLoading =
    loadingRequest || loadingClient || pricingData === undefined;

  const clientProfilePicUrl = clientData?.client?.user?.profilePicUrl ?? '';
  const imageContentWidth = isDesktop ? 307 : width - 20 * 2; // add horizontal padding on mobile
  const profilePicRatio = useImageRatio(clientProfilePicUrl);
  const clientName = `${clientData?.client?.user?.firstName} ${clientData?.client?.user?.lastName}`;

  const aesthetics = getDropdownOptionLabel(
    requestData?.instantStylingRequest.style ?? '',
    STYLE_OPTIONS,
  );
  const expertise = requestData?.instantStylingRequest.reason ?? '';
  const hourlyRate = getHourlyRateFromPricingRequest(
    requestData?.instantStylingRequest.pricingTier ?? PricingTier.Tier1,
    pricingData?.pricing ?? [],
  );
  const duration = getDurationFromInstantEventType(
    requestData?.instantStylingRequest.duration ??
      InstantEventType.SixtyMinutes,
  );
  const priceString = formatCurrency(hourlyRate * (duration / 60));
  const paymentAndDuration = `${priceString} for ${duration} minutes`;
  const clientNotes = requestData?.instantStylingRequest.details ?? '';

  // If match has already expired or moved on, go back to finding client
  useEffect(() => {
    if (!requestData?.instantStylingRequest.stylistId || !user?.stylist?.id) {
      return;
    }

    if (
      timeRemaining === 0 ||
      requestData.instantStylingRequest.stylistId !== user?.stylist?.id
    ) {
      setSnackbarState({
        open: true,
        message: 'This match has already expired.',
        severity: 'info',
      });
      resetMatchStatus();
      router.replace(ROUTES.INSTANT_STYLING.path);
    }
  }, [
    requestData?.instantStylingRequest.stylistId,
    resetMatchStatus,
    setSnackbarState,
    timeRemaining,
    user?.stylist?.id,
  ]);

  const onPressViewProfile = () => {
    setIsClientProfileModalVisible(true);
  };

  const onPressAcceptOrJoinButton = async () => {
    if (!matchStatus) {
      SentryService.captureError('No match status in stylist match found');
      return;
    }

    if (matchStatus.clientAccepted && matchStatus.clientUserId) {
      handleStartSession(matchStatus.clientUserId);
      return;
    }

    if (!matchStatus.requestId || !matchStatus.clientUserId) {
      SentryService.captureError(
        'Missing match status data to accept match in stylist match found',
      );
      return;
    }

    try {
      await stylistAcceptMatch({
        variables: {
          input: {
            requestId: matchStatus.requestId,
            clientUserId: matchStatus.clientUserId,
          },
        },
      });

      setMatchStatus({
        stylistAccepted: true,
      });
    } catch (error) {
      SentryService.captureError(
        'Failed to accept match for stylist match found',
        error,
      );
    }
  };

  const onTimerExpire = async () => {
    if (!matchStatus) {
      SentryService.captureError('No match status in stylist match found');
      return;
    }

    if (!matchStatus.requestId) {
      SentryService.captureError(
        'Missing match status data to decline match in stylist match found',
      );
      return;
    }

    try {
      await stylistDeclineMatch({
        variables: {
          input: {
            requestId: matchStatus.requestId,
            clientUserId: matchStatus.clientUserId,
          },
        },
      });

      resetMatchStatus();
      onStylistDeclineMatch();
    } catch (error) {
      SentryService.captureError(
        'Failed to decline match for stylist match found',
        error,
      );
    }
  };

  const DetailView = ({ label, value }: { label: string; value: string }) => {
    return (
      <DetailViewContainer>
        <DetailLabel>{label}</DetailLabel>
        <DetailValueContainer>
          <DetailValueText>{value}</DetailValueText>
        </DetailValueContainer>
      </DetailViewContainer>
    );
  };

  if (isLoading) {
    return (
      <FlexCenterScreen>
        <ActivityIndicator size="large" />
      </FlexCenterScreen>
    );
  }

  return (
    <OuterContainer isDesktop={isDesktop}>
      {!isDesktop &&
        (matchStatus?.stylistAccepted ? (
          <Title>While Waiting...</Title>
        ) : (
          <Title>Review Clients</Title>
        ))}
      {matchStatus?.stylistAccepted ? (
        <MainText style={{ marginBottom: 36 }}>
          {`Good lighting? Check.${isDesktop ? ' ' : `\n`}WiFi behaving? Check.\nBackground not stealing the show?${isDesktop ? ' ' : `\n`}Tap in and work your styling magic.`}
        </MainText>
      ) : (
        <MainText style={{ marginBottom: 36 }}>
          {`Take a quick look, hit accept,${isDesktop ? ' ' : `\n`}and get styling.`}
        </MainText>
      )}
      {!isDesktop && (
        <AcceptTimerSection
          matchStatus={matchStatus}
          timeRemaining={timeRemaining}
          onPressAcceptOrJoinButton={onPressAcceptOrJoinButton}
          onTimerExpire={onTimerExpire}
        />
      )}
      <ContentContainer>
        {isDesktop && (
          <AcceptTimerSection
            matchStatus={matchStatus}
            timeRemaining={timeRemaining}
            onPressAcceptOrJoinButton={onPressAcceptOrJoinButton}
            onTimerExpire={onTimerExpire}
          />
        )}
        <InnerContainer isDesktop={isDesktop}>
          <ProfilePhotoNameContainer isDesktop={isDesktop}>
            <ProfileImageContainer
              isDesktop={isDesktop}
              width={imageContentWidth}
            >
              <ProfileImage
                source={{ uri: clientProfilePicUrl }}
                style={{ aspectRatio: profilePicRatio }}
              />
            </ProfileImageContainer>
            <ClientNameProfileLinkContainer isDesktop={isDesktop}>
              <ClientNameText>{clientName}</ClientNameText>
              <ViewProfileLink onPress={onPressViewProfile}>
                View Profile
              </ViewProfileLink>
            </ClientNameProfileLinkContainer>
          </ProfilePhotoNameContainer>
          <DetailsContainer isDesktop={isDesktop}>
            <DetailView label="Requested Aesthetics:" value={aesthetics} />
            <DetailView
              label="Requested Expertise:"
              value={expertise.toLowerCase()}
            />
            <DetailView
              label="Estimated Payment & Duration:"
              value={paymentAndDuration}
            />
            <DetailView label="Additional Notes:" value={clientNotes} />
          </DetailsContainer>
        </InnerContainer>
      </ContentContainer>
      <ClientProfileModal
        isVisible={isClientProfileModalVisible}
        setIsVisible={setIsClientProfileModalVisible}
        clientId={clientData?.client?.id ?? 0}
      />
      {!isDesktop && (
        <>
          <Spacer height={42} />
          <BackToTopButton scrollToTop={scrollToTop} />
          <Spacer height={32} />
        </>
      )}
    </OuterContainer>
  );
};

export default StylistMatchFound;

const OuterContainer = styled.View<IsDesktopProps>(({ isDesktop }) => ({
  flex: 1,
  width: '100%',
  backgroundColor: mint,
  borderRadius: isDesktop ? 12 : 0,
  justifyContent: 'center',
  alignItems: 'center',
  paddingTop: 56,
  paddingHorizontal: isDesktop ? 32 : 20,
  paddingBottom: 36,
}));

const ContentContainer = styled.View<IsDesktopProps>(({ isDesktop }) => ({
  width: '100%',
  maxWidth: 872,
  backgroundColor: white,
  borderRadius: 8,
  justifyContent: 'flex-start',
  paddingHorizontal: isDesktop ? 48 : 0,
}));

const Title = styled(H3)({
  textAlign: 'center',
  textTransform: 'uppercase',
  marginBottom: 16,
});

export const MainText = styled(BaseText)({
  fontSize: 16,
  letterSpacing: -0.16,
  textAlign: 'center',
});

const InnerContainer = styled.View<IsDesktopProps>(({ isDesktop }) => ({
  flex: 1,
  flexDirection: isDesktop ? 'row' : 'column',
}));

const ProfilePhotoNameContainer = styled.View<IsDesktopProps>(
  ({ isDesktop }) => ({
    flex: 1,
    width: '100%',
    paddingLeft: isDesktop ? 32 : 0,
  }),
);

type ProfileImageProps = { width: number } & IsDesktopProps;

const ProfileImageContainer = styled.View<ProfileImageProps>(
  ({ width, isDesktop }) => ({
    width,
    borderRadius: 10,
    ...(!isDesktop ? { borderBottomLeftRadius: 0 } : {}),
    ...(!isDesktop ? { borderBottomRightRadius: 0 } : {}),
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    marginBottom: 20,
  }),
);

const ProfileImage = styled.Image({
  width: '100%',
});

const ClientNameProfileLinkContainer = styled.View<IsDesktopProps>(
  ({ isDesktop }) => ({
    alignItems: isDesktop ? 'flex-start' : 'center',
    marginBottom: isDesktop ? 0 : 24,
  }),
);

const ClientNameText = styled(H3)({
  fontSize: 25,
  letterSpacing: -0.25,
  textTransform: 'uppercase',
});

const ViewProfileLink = styled(BaseText)({
  fontSize: 12,
  fontWeight: 'bold',
  letterSpacing: -0.12,
  textDecorationLine: 'underline',
});

const DetailsContainer = styled.View<IsDesktopProps>(({ isDesktop }) => ({
  flex: 1,
  height: '100%',
  ...(isDesktop ? { minWidth: 467 } : {}),
  marginHorizontal: isDesktop ? 32 : 14,
}));

const DetailViewContainer = styled.View({
  width: '100%',
  marginBottom: 36,
});

const DetailLabel = styled(BaseText)({
  fontSize: 12,
  letterSpacing: -0.12,
  marginBottom: 10,
});

const DetailValueContainer = styled.View({
  minHeight: 60,
  backgroundColor: sand,
  borderRadius: 8,
  justifyContent: 'center',
  paddingHorizontal: 16,
  paddingVertical: 16,
});

const DetailValueText = styled(BaseText)({
  fontSize: 15,
  fontWeight: 'bold',
  letterSpacing: -0.15,
  textTransform: 'capitalize',
});
